// Sampling a small bitmap is plenty for colour stats, and a tiny source keeps
// decode + pixel iteration cheap regardless of the original image size.
const SAMPLE_SIZE = 48;

/**
 * Tallies the dominant colours from a sampled `ImageData` buffer.
 *
 * Pixels are quantised into 5-bit-per-channel buckets and counted. Near-white,
 * near-black and washed-out grey pixels are skipped so the result reflects the
 * meaningful colours of the artwork (e.g. the gift card background or uploaded
 * photo) rather than the surrounding paper/text.
 */
function dominantColorsFromPixels(data: Uint8ClampedArray, count: number): string[] {
  const buckets = new Map<number, { count: number; r: number; g: number; b: number }>();
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];

    if (data[i + 3] < 125) continue; // skip transparent pixels
    if (r > 240 && g > 240 && b > 240) continue; // skip near-white (paper)
    if (r < 18 && g < 18 && b < 18) continue; // skip near-black (text)

    const max = r > g ? (r > b ? r : b) : g > b ? g : b;
    const min = r < g ? (r < b ? r : b) : g < b ? g : b;
    if (max - min < 12 && max > 200) continue; // skip washed-out greys/highlights

    const key = ((r >> 3) << 10) | ((g >> 3) << 5) | (b >> 3);
    const entry = buckets.get(key);
    if (entry) {
      entry.count++;
      entry.r += r;
      entry.g += g;
      entry.b += b;
    } else {
      buckets.set(key, { count: 1, r, g, b });
    }
  }

  return [...buckets.values()]
    .sort((a, b) => b.count - a.count)
    .slice(0, count)
    .map((e) => `rgb(${Math.round(e.r / e.count)}, ${Math.round(e.g / e.count)}, ${Math.round(e.b / e.count)})`);
}

/**
 * Extracts the dominant colors from an image.
 *
 * The image is decoded and downscaled to a small bitmap via `createImageBitmap`
 * (off the main thread, with native resizing so the full-resolution source is
 * never drawn), then its pixels are quantised into colour buckets. The most
 * frequent buckets are returned as `rgb(...)` strings. Returns `[]` on any
 * failure (e.g. tainted cross-origin canvas) so the caller can fall back.
 */
export async function extractDominantColors(
  imageUrl: string,
  count = 3
): Promise<string[]> {
  try {
    const response = await fetch(imageUrl, { mode: 'cors' });
    const blob = await response.blob();
    const bitmap = await createImageBitmap(blob, {
      resizeWidth: SAMPLE_SIZE,
      resizeHeight: SAMPLE_SIZE,
      resizeQuality: 'low',
    });

    const canvas = document.createElement('canvas');
    canvas.width = SAMPLE_SIZE;
    canvas.height = SAMPLE_SIZE;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) {
      bitmap.close();
      return [];
    }
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close();

    const { data } = ctx.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
    return dominantColorsFromPixels(data, count);
  } catch {
    // Network/CORS/decode failure or tainted canvas — let the caller fall back.
    return [];
  }
}
